import { notifyModalOpen, notifyModalClose } from '../../utils/modalState';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Shield, UserPlus, X, Search, Users, ChevronRight, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { filterPlayersByPositionMapping } from '../../services/positionMappingService';
import type { Player } from '../../types';
import type { Sport } from '../../types';
import { InjuryBadge } from '../ui/InjuryBadge';
import { INJURY_STATUS } from '../../constants/injuries';

interface FormationPosition {
  id: string;
  label: string;
  fullName?: string;
  x: number;
  y: number;
  positionGroupKey?: string;
  color?: string;
  isCenter?: boolean;
}

interface DepthChartEntry {
  id?: string;
  player_id: string | null;
  position: string;
  depth: number;
  color?: string;
}

interface FormationDepthChartProps {
  teamId: string;
  sport: Sport;
  unit: 'Offense' | 'Defense' | 'Special Teams';
  formationId?: string;
  formationName?: string;
  players: Player[];
  onPlayerAssign: (positionLabel: string, depth: number, playerId: string | null) => void;
  currentDepthChart?: DepthChartEntry[];
  depthStrings: { id: number; name: string; active: boolean }[];
  activeDepth: number;
}

const FormationDepthChart: React.FC<FormationDepthChartProps> = ({
  teamId,
  sport,
  unit,
  formationId,
  formationName,
  players,
  onPlayerAssign,
  currentDepthChart = [],
  depthStrings,
  activeDepth
}) => {
  const [formationPositions, setFormationPositions] = useState<FormationPosition[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPosition, setSelectedPosition] = useState<FormationPosition | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Load formation positions
  useEffect(() => {
    const loadFormation = async () => {
      setIsLoading(true);
      try {
        // First, try to load from custom_formations (custom formations)
        if (formationId || formationName) {
          let query = supabase
            .from('custom_formations')
            .select('positions, name')
            .eq('team_id', teamId)
            .eq('sport', sport)
            .eq('unit', unit);

          if (formationId) {
            query = query.eq('id', formationId);
          } else if (formationName) {
            query = query.eq('name', formationName);
          }

          const { data, error } = await query.single();

          if (!error && data?.positions) {
            // Found custom formation
            const positions: FormationPosition[] = (data.positions || []).map((pos: any) => ({
              id: pos.id || `pos_${Date.now()}`,
              label: (pos.label || pos.name || '').toUpperCase(),
              fullName: pos.fullName || pos.full_name || pos.label || pos.name || '',
              x: pos.x || pos.x_coordinate || 50,
              y: pos.y || pos.y_coordinate || 50,
              positionGroupKey: pos.positionGroupKey || pos.position_group_key,
              color: pos.color || '#1E40AF',
              isCenter: pos.isCenter || false,
            }));
            setFormationPositions(positions);
            setIsLoading(false);
            return;
          }
        }

        // If not found in custom_formations, try to load predefined formation
        if (formationName) {
          const { 
            FOOTBALL_OFFENSIVE_FORMATIONS,
            FOOTBALL_DEFENSIVE_FORMATIONS,
            FOOTBALL_SPECIAL_TEAMS_FORMATIONS,
            FLAG_FOOTBALL_OFFENSIVE_FORMATIONS,
            SOCCER_FORMATIONS
          } = await import('../../data/sportsFormations');
          
          // Get formations based on sport and unit
          let predefinedFormations: any[] = [];
          if (sport === 'Football') {
            if (unit === 'Offense') {
              predefinedFormations = FOOTBALL_OFFENSIVE_FORMATIONS;
            } else if (unit === 'Defense') {
              predefinedFormations = FOOTBALL_DEFENSIVE_FORMATIONS;
            } else if (unit === 'Special Teams') {
              predefinedFormations = FOOTBALL_SPECIAL_TEAMS_FORMATIONS;
            }
          } else if (sport === 'FlagFootball') {
            if (unit === 'Offense') {
              predefinedFormations = FLAG_FOOTBALL_OFFENSIVE_FORMATIONS;
            }
          } else if (sport === 'Soccer') {
            predefinedFormations = SOCCER_FORMATIONS;
          }
          
          const predefinedFormation = predefinedFormations.find(f => f.name === formationName);

          if (predefinedFormation) {
            // Convert predefined formation to FormationPosition format
            const positions: FormationPosition[] = predefinedFormation.positions.map((pos, index) => {
              // Try to infer position group
              let positionGroupKey: string | undefined;
              let defaultColor = '#1E40AF';
              
              // Offensive positions
              if (pos.key === 'QB') {
                positionGroupKey = 'QB';
                defaultColor = '#DC2626';
              } else if (['RB', 'FB', 'HB'].includes(pos.key)) {
                positionGroupKey = 'RB';
              } else if (['X', 'Z', 'Y', 'SL', 'H', 'WR'].includes(pos.key)) {
                positionGroupKey = 'WR';
              } else if (['Y', 'TE', 'U'].includes(pos.key)) {
                positionGroupKey = 'TE';
              } else if (['LT', 'LG', 'C', 'RG', 'RT'].includes(pos.key)) {
                positionGroupKey = 'OL';
              }
              // Defensive positions
              else if (['LDE', 'RDE', 'LDT', 'RDT', 'NT', 'DE', 'DT', 'EDGE'].includes(pos.key)) {
                positionGroupKey = 'DL';
              } else if (['WLB', 'MLB', 'SLB', 'LOLB', 'ROLB', 'LILB', 'RILB', 'ILB1', 'ILB2', 'OLB1', 'OLB2', 'LB'].includes(pos.key)) {
                positionGroupKey = 'LB';
              } else if (['LCB', 'RCB', 'FS', 'SS', 'NB', 'CB', 'S'].includes(pos.key)) {
                positionGroupKey = 'DB';
              }

              return {
                id: `pos_${index}_${Date.now()}`,
                label: pos.name.toUpperCase(),
                fullName: pos.fullName || pos.name,
                x: 50, // Default position (will be adjusted by visual builder if needed)
                y: 50,
                positionGroupKey,
                color: pos.color || defaultColor,
                isCenter: pos.key === 'C',
              };
            });

            setFormationPositions(positions);
            setIsLoading(false);
            return;
          }
        }

        // No formation found
        setFormationPositions([]);
      } catch (error) {
        console.error('Error loading formation:', error);
        setFormationPositions([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadFormation();
  }, [teamId, sport, unit, formationId, formationName]);

  // Get assigned player for a position
  const getAssignedPlayer = useCallback((positionLabel: string): Player | null => {
    const entry = currentDepthChart.find(
      e => e.position === positionLabel && e.depth === activeDepth
    );
    if (!entry?.player_id) return null;
    return players.find(p => p.id === entry.player_id) || null;
  }, [currentDepthChart, players, activeDepth]);

  // Get all assigned player IDs for the active depth
  const assignedPlayerIds = useMemo(() => {
    return new Set(
      currentDepthChart
        .filter(e => e.depth === activeDepth && e.player_id)
        .map(e => e.player_id)
    );
  }, [currentDepthChart, activeDepth]);

  // Get bench players (not assigned to active depth)
  const benchPlayers = useMemo(() => {
    return players.filter(p => !assignedPlayerIds.has(p.id));
  }, [players, assignedPlayerIds]);

  // Sort positions: Line positions first, then skill positions
  const sortedFormationPositions = useMemo(() => {
    const linePositionGroups = ['OL', 'DL', 'EDGE'];
    
    // Define order for OL positions (left to right: LT, LG, C, RG, RT)
    const olPositionOrder: Record<string, number> = {
      'LT': 1, 'LEFT TACKLE': 1,
      'LG': 2, 'LEFT GUARD': 2,
      'C': 3, 'CENTER': 3,
      'RG': 4, 'RIGHT GUARD': 4,
      'RT': 5, 'RIGHT TACKLE': 5,
    };
    
    // Define order for DL positions
    const dlPositionOrder: Record<string, number> = {
      'DT': 1, 'DEFENSIVE TACKLE': 1,
      'NT': 2, 'NOSE TACKLE': 2,
      'DE': 3, 'DEFENSIVE END': 3,
      'DL': 1,
    };
    
    // Define order within skill positions
    const skillPositionOrder: Record<string, number> = {
      'QB': 1, 'QUARTERBACK': 1,
      'RB': 2, 'RUNNING BACK': 2, 'FB': 2, 'FULLBACK': 2, 'HB': 2, 'HALFBACK': 2,
      'WR': 3, 'WIDE RECEIVER': 3, 'X': 3, 'Z': 3, 'Y': 3, 'SL': 3, 'SLOT': 3, 'H': 3,
      'TE': 4, 'TIGHT END': 4, 'U': 4,
      'DB': 5, 'DEFENSIVE BACK': 5, 'CB': 5, 'CORNERBACK': 5,
      'S': 6, 'SAFETY': 6, 'SS': 6, 'STRONG SAFETY': 6, 'FS': 6, 'FREE SAFETY': 6,
      'LB': 7, 'LINEBACKER': 7, 'MLB': 7, 'MIDDLE LINEBACKER': 7, 'OLB': 7, 'OUTSIDE LINEBACKER': 7, 'ILB': 7, 'INSIDE LINEBACKER': 7,
    };
    
    return [...formationPositions].sort((a, b) => {
      const aGroup = (a.positionGroupKey || '').toUpperCase();
      const bGroup = (b.positionGroupKey || '').toUpperCase();
      const aLabel = a.label.toUpperCase();
      const bLabel = b.label.toUpperCase();
      
      const aIsLine = linePositionGroups.includes(aGroup);
      const bIsLine = linePositionGroups.includes(bGroup);
      
      // Line positions come first
      if (aIsLine && !bIsLine) return -1;
      if (!aIsLine && bIsLine) return 1;
      
      // If both are line positions, sort by specific line order
      if (aIsLine && bIsLine) {
        // OL positions
        if (aGroup === 'OL' && bGroup === 'OL') {
          const aOrder = olPositionOrder[aLabel] || 999;
          const bOrder = olPositionOrder[bLabel] || 999;
          if (aOrder !== bOrder) return aOrder - bOrder;
        }
        // DL positions
        else if ((aGroup === 'DL' || aGroup === 'EDGE') && (bGroup === 'DL' || bGroup === 'EDGE')) {
          const aOrder = dlPositionOrder[aLabel] || dlPositionOrder[aGroup] || 999;
          const bOrder = dlPositionOrder[bLabel] || dlPositionOrder[bGroup] || 999;
          if (aOrder !== bOrder) return aOrder - bOrder;
        }
        // Different line groups: OL before DL
        else if (aGroup === 'OL' && bGroup !== 'OL') return -1;
        else if (aGroup !== 'OL' && bGroup === 'OL') return 1;
        
        // If same order, sort by label
        return aLabel.localeCompare(bLabel);
      }
      
      // If both are skill positions, sort by skill order
      if (!aIsLine && !bIsLine) {
        const aOrder = skillPositionOrder[aLabel] || skillPositionOrder[aGroup] || 999;
        const bOrder = skillPositionOrder[bLabel] || skillPositionOrder[bGroup] || 999;
        if (aOrder !== bOrder) return aOrder - bOrder;
        // If same order, sort by label
        return aLabel.localeCompare(bLabel);
      }
      
      // Fallback: sort by label
      return aLabel.localeCompare(bLabel);
    });
  }, [formationPositions]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="loading loading-spinner loading-lg"></div>
      </div>
    );
  }

  if (formationPositions.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p className="text-lg font-medium mb-2">No formation selected</p>
        <p className="text-sm">Please select a formation to view positions</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Starting Lineup Section */}
      <div className="bg-white rounded-lg border-2 border-gray-200">
        <div className="p-4 border-b bg-gray-50">
          <h2 className="text-lg font-bold text-gray-900">EDIT STARTERS</h2>
          <p className="text-sm text-gray-600 mt-1">{depthStrings.find(d => d.id === activeDepth)?.name || 'Active Depth'}</p>
        </div>
        
        <div className="divide-y divide-gray-200">
          {sortedFormationPositions.map((position) => {
            const player = getAssignedPlayer(position.label);
            return (
              <PositionRow
                key={position.id}
                position={position}
                player={player}
                onSelect={() => setSelectedPosition(position)}
                onRemove={() => {
                  onPlayerAssign(position.label, activeDepth, null);
                }}
                teamId={teamId}
                sport={sport}
                unit={unit}
                formationId={formationId}
                formationName={formationName}
                eligiblePlayers={players}
                currentDepthChart={currentDepthChart}
                activeDepth={activeDepth}
              />
            );
          })}
        </div>
      </div>

      {/* Bench Section */}
      <div className="bg-white rounded-lg border-2 border-gray-200">
        <div className="p-4 border-b bg-gray-50">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Users className="w-5 h-5" />
            EDIT BENCH
          </h2>
          <p className="text-sm text-gray-600 mt-1">{benchPlayers.length} available players</p>
        </div>
        
        {benchPlayers.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p className="font-medium">No available players</p>
            <p className="text-sm mt-2">All players are assigned to the lineup</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {benchPlayers.map((player) => (
              <BenchPlayerRow key={player.id} player={player} />
            ))}
          </div>
        )}
      </div>

      {/* Player Selection Modal */}
      {selectedPosition && (
        <PlayerSelectionModal
          position={selectedPosition}
          onClose={() => setSelectedPosition(null)}
          onSelect={(playerId) => {
            onPlayerAssign(selectedPosition.label, activeDepth, playerId);
            setSelectedPosition(null);
          }}
          teamId={teamId}
          sport={sport}
          unit={unit}
          formationId={formationId}
          formationName={formationName}
          eligiblePlayers={players}
        />
      )}
    </div>
  );
};

interface PositionRowProps {
  position: FormationPosition;
  player: Player | null;
  onSelect: () => void;
  onRemove: () => void;
  teamId: string;
  sport: Sport;
  unit: 'Offense' | 'Defense' | 'Special Teams';
  formationId?: string;
  formationName?: string;
  eligiblePlayers: Player[];
  currentDepthChart: DepthChartEntry[];
  activeDepth: number;
}

const PositionRow: React.FC<PositionRowProps> = ({
  position,
  player,
  onSelect,
  onRemove,
}) => {
  return (
    <div
      onClick={onSelect}
      className="w-full p-4 hover:bg-gray-50 transition-colors cursor-pointer"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      <div className="flex items-center gap-4">
        {/* Position Button */}
        <div
          className={`${position.isCenter ? 'rounded' : 'rounded-full'} w-12 h-12 flex items-center justify-center text-white font-bold text-sm flex-shrink-0`}
          style={{ backgroundColor: position.color || '#1E40AF' }}
        >
          {position.label}
        </div>

        {/* Player Info or Empty Slot */}
        {player ? (
          <div className="flex-1 flex items-center justify-between min-w-0">
            <div className="flex-1 min-w-0">
              <div
                className="font-semibold text-base text-gray-900 flex items-center gap-1.5 flex-wrap min-w-0"
                title={`${player.first_name} ${player.last_name}`}
              >
                <span className="break-words min-w-0">
                  {player.first_name} {player.last_name}
                </span>
                {(player as any).injury_status && (
                  <InjuryBadge
                    injuryStatus={(player as any).injury_status}
                    className="flex-shrink-0"
                  />
                )}
              </div>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className="text-sm text-gray-600">#{player.number}</span>
                {(player as any).playParticipation?.hasMPR && (
                  <div className="flex items-center gap-1 bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded text-xs font-medium">
                    <Shield className="w-3 h-3" />
                    MPR {(player as any).playParticipation?.minimumRequiredPlays || 0}
                  </div>
                )}
                {(player as any).eligible_positions && (
                  <span className="text-xs text-gray-500">
                    {(player as any).eligible_positions.join(', ')}
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              className="btn btn-ghost btn-sm btn-circle flex-shrink-0 ml-2"
              title="Remove player"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-between">
            <div className="flex items-center gap-2 text-gray-500">
              <UserPlus className="w-5 h-5" />
              <span className="text-sm">Tap to select player</span>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </div>
        )}
      </div>
    </div>
  );
};

interface BenchPlayerRowProps {
  player: Player;
}

const BenchPlayerRow: React.FC<BenchPlayerRowProps> = ({ player }) => {
  return (
    <div className="p-4">
      <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                      <span className="font-bold text-gray-900 text-sm">#{player.number || '?'}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div
                        className="font-semibold text-base text-gray-900 flex items-center gap-1.5 flex-wrap min-w-0"
                        title={`${player.first_name || ''} ${player.last_name || 'Unknown Player'}`.trim()}
                      >
                        <span className="break-words min-w-0">
                          {player.first_name || ''} {player.last_name || 'Unknown Player'}
                        </span>
                        {(player as any).injury_status && (
                          <InjuryBadge
                            injuryStatus={(player as any).injury_status}
                            className="flex-shrink-0"
                          />
                        )}
                      </div>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {(player as any).playParticipation?.hasMPR && (
              <div className="flex items-center gap-1 bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded text-xs font-medium">
                <Shield className="w-3 h-3" />
                MPR {(player as any).playParticipation?.minimumRequiredPlays || 0}
              </div>
            )}
            {(player as any).eligible_positions && (
              <span className="text-xs text-gray-500">
                {(player as any).eligible_positions.join(', ')}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

interface PlayerSelectionModalProps {
  position: FormationPosition;
  onClose: () => void;
  onSelect: (playerId: string) => void;
  teamId: string;
  sport: Sport;
  unit: 'Offense' | 'Defense' | 'Special Teams';
  formationId?: string;
  formationName?: string;
  eligiblePlayers: Player[];
}

const PlayerSelectionModal: React.FC<PlayerSelectionModalProps> = ({
  position,
  onClose,
  onSelect,
  teamId,
  sport,
  unit,
  formationId,
  formationName,
  eligiblePlayers,
}) => {
  const [filteredPlayers, setFilteredPlayers] = useState<Player[]>([]);
  const [isLoadingPlayers, setIsLoadingPlayers] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    notifyModalOpen();
    return () => notifyModalClose();
  }, []);

  // Load eligible players based on position mappings
  useEffect(() => {
    const loadEligiblePlayers = async () => {
      setIsLoadingPlayers(true);
      try {
        console.log('[PlayerSelectionModal] Filtering players for position:', position.label);
        console.log('[PlayerSelectionModal] Position group key from formation:', position.positionGroupKey);
        console.log('[PlayerSelectionModal] Total eligible players:', eligiblePlayers.length);
        
        // First, try to get position groups from mappings
        const { getPositionGroupsForLabel } = await import('../../services/positionMappingService');
        const mappedGroups = await getPositionGroupsForLabel(
          teamId,
          sport,
          unit,
          position.label,
          formationId,
          formationName
        );
        
        console.log('[PlayerSelectionModal] Mapped groups from database:', mappedGroups);
        
        // If no mappings found, use the positionGroupKey from the formation position as fallback
        let groupsToFilter: string[] = mappedGroups;
        if (mappedGroups.length === 0 && position.positionGroupKey) {
          console.log('[PlayerSelectionModal] No mappings found, using positionGroupKey from formation:', position.positionGroupKey);
          groupsToFilter = [position.positionGroupKey];
        }
        
        // If still no groups, we can't filter - show empty or all players?
        // For now, if no mappings and no positionGroupKey, show all (backward compatibility)
        // But log a warning
        if (groupsToFilter.length === 0) {
          console.warn('[PlayerSelectionModal] No position mappings found and no positionGroupKey. Showing all players.');
          setFilteredPlayers(eligiblePlayers);
          setIsLoadingPlayers(false);
          return;
        }
        
        console.log('[PlayerSelectionModal] Filtering by groups:', groupsToFilter);
        
        // Check if we're filtering for OL positions (line positions)
        const isLinePosition = groupsToFilter.includes('OL') || groupsToFilter.includes('DL') || groupsToFilter.includes('EDGE');
        
        // Filter players who have at least one of the mapped position groups
        // OR have can_play_line: true if filtering for line positions
        const filteredPlayerObjects = eligiblePlayers.filter(player => {
          const playerGroups = (player as any).eligible_positions || [];
          const canPlayLine = (player as any).can_play_line || false;
          
          // Check if player matches by position group
          const matchesByGroup = playerGroups.some((group: string) => groupsToFilter.includes(group));
          
          // If filtering for line positions, also include players with can_play_line: true
          const matchesByLineDesignation = isLinePosition && canPlayLine;
          
          const matches = matchesByGroup || matchesByLineDesignation;
          
          if (matches) {
            const matchReason = matchesByGroup 
              ? `position groups: ${playerGroups.join(', ')}`
              : `can_play_line: true`;
            console.log(`[PlayerSelectionModal] Player ${player.first_name} ${player.last_name} matches (${matchReason})`);
          }
          return matches;
        });
        
        console.log('[PlayerSelectionModal] Filtered player objects:', filteredPlayerObjects.length);
        console.log('[PlayerSelectionModal] Filtered players:', filteredPlayerObjects.map(p => `${p.first_name} ${p.last_name} (${(p as any).eligible_positions?.join(', ') || 'no groups'})`));

        // Note: players already assigned to this position at other depths are intentionally
        // still shown here, so coaches can freely swap the same player across strings/depths
        // and across formations without the picker hiding them.
        setFilteredPlayers(filteredPlayerObjects);
      } catch (error) {
        console.error('Error loading eligible players:', error);
        // Fallback: if filtering fails, show all players (but log the error)
        setFilteredPlayers(eligiblePlayers);
      } finally {
        setIsLoadingPlayers(false);
      }
    };
    loadEligiblePlayers();
  }, [position.label, position.positionGroupKey, teamId, sport, unit, eligiblePlayers, formationId, formationName]);

  // Filter by search query
  const displayPlayers = useMemo(() => {
    if (!searchQuery) return filteredPlayers;
    const query = searchQuery.toLowerCase();
    return filteredPlayers.filter(p =>
      p.first_name.toLowerCase().includes(query) ||
      p.last_name.toLowerCase().includes(query) ||
      p.number?.toString().includes(query) ||
      (p as any).eligible_positions?.some((pos: string) => pos.toLowerCase().includes(query))
    );
  }, [filteredPlayers, searchQuery]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className={`${position.isCenter ? 'rounded' : 'rounded-full'} w-10 h-10 flex items-center justify-center text-white font-bold text-sm flex-shrink-0`}
              style={{ backgroundColor: position.color || '#1E40AF' }}
            >
              {position.label}
            </div>
            <div>
              <h3 className="font-semibold text-lg text-gray-900">
                Select {position.label}
              </h3>
              {position.fullName && (
                <p className="text-sm text-gray-500">{position.fullName}</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="btn btn-ghost btn-sm btn-circle flex-shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search players..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input input-bordered w-full pl-10"
            />
          </div>
        </div>

        {/* Player List */}
        <div className="flex-1 overflow-y-auto">
          {isLoadingPlayers ? (
            <div className="flex justify-center py-12">
              <div className="loading loading-spinner loading-md"></div>
            </div>
          ) : displayPlayers.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p className="font-medium">No eligible players found</p>
              <p className="text-sm mt-2">
                {searchQuery 
                  ? 'Try a different search term'
                  : 'Make sure players have the correct position groups assigned'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {errorMessage && (
                <div className="p-4 bg-red-50 border-l-4 border-red-400 mb-2">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                    <p className="text-sm text-red-800 font-medium">{errorMessage}</p>
                  </div>
                </div>
              )}
              {displayPlayers.map((player) => {
                const isOut = (player as any).injury_status === INJURY_STATUS.OUT;
                const playerName = `${player.first_name || ''} ${player.last_name || 'Unknown Player'}`.trim();
                
                return (
                  <button
                    key={player.id}
                    onClick={() => {
                      if (isOut) {
                        setErrorMessage(`Cannot select ${playerName}. This player is currently listed as out due to injury.`);
                        // Clear error message after 5 seconds
                        setTimeout(() => setErrorMessage(null), 5000);
                        return;
                      }
                      setErrorMessage(null);
                      onSelect(player.id);
                    }}
                    disabled={isOut}
                    className={`w-full p-4 text-left transition-colors ${
                      isOut 
                        ? 'bg-red-50 hover:bg-red-100 cursor-not-allowed opacity-75' 
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isOut ? 'bg-red-200' : 'bg-gray-200'
                      }`}>
                        <span className={`font-bold text-sm ${
                          isOut ? 'text-red-900' : 'text-gray-900'
                        }`}>
                          #{player.number || '?'}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div
                          className={`font-semibold text-base flex items-center gap-1.5 flex-wrap min-w-0 ${
                            isOut ? 'text-red-900' : 'text-gray-900'
                          }`}
                          title={playerName}
                        >
                          <span className="break-words min-w-0">{playerName}</span>
                          {(player as any).injury_status && (
                            <InjuryBadge
                              injuryStatus={(player as any).injury_status}
                              className="flex-shrink-0"
                            />
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          {(player as any).playParticipation?.hasMPR && (
                            <div className="flex items-center gap-1 bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded text-xs font-medium">
                              <Shield className="w-3 h-3" />
                              MPR {(player as any).playParticipation?.minimumRequiredPlays || 0}
                            </div>
                          )}
                          {(player as any).eligible_positions && (player as any).eligible_positions.length > 0 && (
                            <span className="text-xs text-gray-500">
                              {(player as any).eligible_positions.join(', ')}
                            </span>
                          )}
                        </div>
                      </div>
                      {isOut ? (
                        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormationDepthChart;
